/* ================================================================
   GAYNOR MINDEN — Pointe Shoe Configurator Core
   Shared by DTC (Shopify Liquid) and B2B portal
   
   Loads pointe-sku-lookup.json and drives:
   - Dependent dropdowns (options narrow as you select)
   - Live SKU validation (only allow Add to Cart if SKU exists)
   - Live SKU preview string
   ================================================================ */

'use strict';

const GM_CONFIG = (() => {

  let LOOKUP = null;  // loaded from JSON

  // ── Load lookup data ────────────────────────────────────────────
  async function loadLookup(jsonUrl) {
    if (LOOKUP) return LOOKUP;
    const resp = await fetch(jsonUrl);
    LOOKUP = await resp.json();
    return LOOKUP;
  }

  // ── Format size: 8.5 → '08.5', 8 → '08.0' ─────────────────────
  function formatSize(size) {
    const n = parseFloat(size);
    const i = Math.floor(n);
    const d = (n % 1 === 0.5) ? 5 : 0;
    return (i < 10 ? '0' + i : '' + i) + '.' + d;
  }

  // ── Build SKU string from selections ───────────────────────────
  function buildSKU(shoe, selections) {
    const { model, length, width, box, shank, vamp, heel } = selections;

    if (shoe === 'europa' || shoe === 'lyra') {
      const prefix = shoe === 'europa' ? 'E' : 'L';
      if (model && length && width && box && shank && vamp && heel) {
        return `${prefix}${model}-${formatSize(length)}${width}${box}${shank}${vamp}${heel}`;
      }
      // Partial preview
      if (model) {
        const parts = [length && formatSize(length), width, box, shank, vamp, heel].filter(Boolean);
        return parts.length ? `${prefix}${model}-${parts.join('')}…` : null;
      }
      return null;
    }

    if (shoe === 'renaissance' || shoe === 'primavera') {
      const pfx = shoe === 'renaissance' ? 'REN' : 'PRI';
      if (length && width && shank) {
        return `${pfx}-${width}-${formatSize(length)}-${shank}`;
      }
      const parts = [width, length && formatSize(length), shank].filter(Boolean);
      return parts.length ? `${pfx}-${parts.join('-')}…` : null;
    }
    return null;
  }

  // ── Validate a complete SKU ─────────────────────────────────────
  function isValidSKU(shoe, sku) {
    if (!LOOKUP || !sku || sku.endsWith('…')) return false;
    return LOOKUP[shoe]?.skus?.includes(sku) ?? false;
  }

  // ── Get available options given current selections ──────────────
  // Dependent dropdowns:
  //   - Length depends on Model (CL=20 sizes, SC=14, SK=11)
  //   - Box depends on Width (Narrow gets 3,3+,4 only — no #5)
  function getOptions(shoe, field, currentSelections) {
    if (!LOOKUP) return [];
    const data = LOOKUP[shoe];
    if (!data) return [];

    if (shoe === 'europa' || shoe === 'lyra') {
      const modelCode = currentSelections.model;

      if (field === 'model') {
        return Object.entries(data.models).map(([code, m]) => ({
          value: code, label: m.label
        }));
      }

      if (!modelCode || !data.models[modelCode]) return [];
      const modelData = data.models[modelCode];

      if (field === 'length') return modelData.lengths.map(l => ({ value: l, label: l }));
      if (field === 'width')  return modelData.widths;
      if (field === 'box') {
        // Filter boxes based on selected width
        const widthCode = currentSelections.width;
        if (widthCode && modelData.widthToBoxes && modelData.widthToBoxes[widthCode]) {
          const allowed = modelData.widthToBoxes[widthCode];
          return modelData.boxes.filter(b => allowed.includes(b.value));
        }
        return modelData.boxes;
      }
      if (field === 'shank')  return modelData.shanks;
      if (field === 'vamp')   return modelData.vamps;
      if (field === 'heel')   return modelData.heels;
    }

    if (shoe === 'renaissance' || shoe === 'primavera') {
      if (field === 'length') return data.lengths.map(l => ({ value: l, label: l }));
      if (field === 'width')  return data.widths;
      if (field === 'shank')  return data.shanks;
    }

    return [];
  }

  // ── Populate a <select> with options ───────────────────────────
  function populateSelect(selectEl, options, currentValue, placeholder) {
    const prev = currentValue || selectEl.value;
    selectEl.innerHTML = '';

    if (placeholder) {
      const opt = document.createElement('option');
      opt.value = '';
      opt.textContent = '— Select —';
      selectEl.appendChild(opt);
    }

    options.forEach(o => {
      const opt = document.createElement('option');
      opt.value = o.value;
      opt.textContent = o.label;
      selectEl.appendChild(opt);
    });

    // Restore previous value if still valid
    if (prev && [...selectEl.options].some(o => o.value === prev)) {
      selectEl.value = prev;
    } else {
      selectEl.value = '';
    }
  }

  // ── Wire up a complete Europa/Lyra configurator form ───────────
  // Expects IDs: cfg-model, cfg-length, cfg-width, cfg-box,
  //              cfg-shank-el, cfg-vamp, cfg-heel, cfg-qty
  // Outputs to: sku-preview, sku-preview-value, modal-unit-price
  // priceMap: { europa: cents, lyra: cents } (from Shopify product JS)
  function wireEuropaLyra(shoe, opts = {}) {
    const {
      modelId    = 'cfg-model',
      lengthId   = 'cfg-length',
      widthId    = 'cfg-width',
      boxId      = 'cfg-box',
      shankId    = 'cfg-shank-el',
      vampId     = 'cfg-vamp',
      heelId     = 'cfg-heel',
      skuPreview = 'sku-preview',
      skuValue   = 'sku-preview-value',
      unitPrice  = 'modal-unit-price',
      addBtn     = 'btn-modal-add',
      priceFormatted = null,
    } = opts;

    const get = id => document.getElementById(id);

    function getSelections() {
      return {
        model:  get(modelId)?.value  || '',
        length: get(lengthId)?.value || '',
        width:  get(widthId)?.value  || '',
        box:    get(boxId)?.value    || '',
        shank:  get(shankId)?.value  || '',
        vamp:   get(vampId)?.value   || '',
        heel:   get(heelId)?.value   || '',
      };
    }

    function update() {
      const sel = getSelections();

      // Repopulate length dropdown when model changes
      const lengths = getOptions(shoe, 'length', sel);
      if (get(lengthId)) populateSelect(get(lengthId), lengths, sel.length, true);

      // Repopulate box dropdown when width changes (Narrow has no #5)
      const boxes = getOptions(shoe, 'box', sel);
      if (get(boxId)) populateSelect(get(boxId), boxes, sel.box, true);

      // Build SKU
      const sku = buildSKU(shoe, sel);
      const valid = sku && !sku.endsWith('…') && isValidSKU(shoe, sku);

      const previewEl  = get(skuPreview);
      const previewVal = get(skuValue);
      const addBtnEl   = get(addBtn);

      if (sku && previewEl && previewVal) {
        previewEl.style.display = 'flex';
        previewVal.textContent  = sku;
        previewVal.style.color  = valid ? 'var(--charcoal)' : 'var(--amber)';
      } else if (previewEl) {
        previewEl.style.display = 'none';
      }

      // Enable/disable add button
      if (addBtnEl) {
        addBtnEl.disabled = !valid;
        addBtnEl.style.opacity = valid ? '1' : '0.5';
      }

      return { sku, valid };
    }

    // Bind change events
    [modelId, widthId, boxId, shankId, vampId, heelId].forEach(id => {
      get(id)?.addEventListener('change', update);
    });
    // Model change also repopulates lengths
    get(modelId)?.addEventListener('change', () => {
      if (get(lengthId)) get(lengthId).value = '';
      update();
    });
    // Width change repopulates boxes (Narrow has no #5)
    get(widthId)?.addEventListener('change', () => {
      if (get(boxId)) get(boxId).value = '';
      update();
    });
    get(lengthId)?.addEventListener('change', update);

    // Initial population
    if (LOOKUP) {
      const modelOpts = getOptions(shoe, 'model', {});
      if (get(modelId)) populateSelect(get(modelId), modelOpts, '', true);
    }

    update();
    return { getSelections, update, buildSKU: (sel) => buildSKU(shoe, sel), isValid: (sku) => isValidSKU(shoe, sku) };
  }

  // ── Wire up a Ren/Pri configurator ─────────────────────────────
  function wireRenPri(shoe, opts = {}) {
    const {
      lengthId   = 'cfg-rp-length',
      widthId    = 'cfg-rp-width',
      shankId    = 'cfg-rp-shank',
      skuPreview = 'sku-preview',
      skuValue   = 'sku-preview-value',
      addBtn     = 'btn-modal-add',
    } = opts;

    const get = id => document.getElementById(id);

    function getSelections() {
      return {
        length: get(lengthId)?.value || '',
        width:  get(widthId)?.value  || '',
        shank:  get(shankId)?.value  || '',
      };
    }

    function update() {
      const sel = getSelections();
      const sku = buildSKU(shoe, sel);
      const valid = sku && !sku.endsWith('…') && isValidSKU(shoe, sku);

      const previewEl  = get(skuPreview);
      const previewVal = get(skuValue);
      const addBtnEl   = get(addBtn);

      if (sku && previewEl && previewVal) {
        previewEl.style.display = 'flex';
        previewVal.textContent  = sku;
        previewVal.style.color  = valid ? 'var(--charcoal)' : 'var(--amber)';
      } else if (previewEl) {
        previewEl.style.display = 'none';
      }

      if (addBtnEl) {
        addBtnEl.disabled = !valid;
        addBtnEl.style.opacity = valid ? '1' : '0.5';
      }

      return { sku, valid };
    }

    // Initial population
    if (LOOKUP) {
      const lengths = getOptions(shoe, 'length', {});
      const widths  = getOptions(shoe, 'width',  {});
      const shanks  = getOptions(shoe, 'shank',  {});
      if (get(lengthId)) populateSelect(get(lengthId), lengths, '', true);
      if (get(widthId))  populateSelect(get(widthId),  widths,  '', true);
      if (get(shankId))  populateSelect(get(shankId),  shanks,  '', true);
    }

    [lengthId, widthId, shankId].forEach(id => {
      get(id)?.addEventListener('change', update);
    });

    update();
    return { getSelections, update, buildSKU: (sel) => buildSKU(shoe, sel), isValid: (sku) => isValidSKU(shoe, sku) };
  }

  return { loadLookup, buildSKU, isValidSKU, getOptions, populateSelect, wireEuropaLyra, wireRenPri, formatSize };
})();
